"use client";

import { useParams, useRouter } from "next/navigation";

import { defaultLocale, type Locales, locales } from "../locales";

type LocalizedRouter = {
  push: (
    href: string,
    options?: Parameters<ReturnType<typeof useRouter>["push"]>[1]
  ) => void;
  replace: (
    href: string,
    options?: Parameters<ReturnType<typeof useRouter>["replace"]>[1]
  ) => void;
  back: () => void;
  forward: () => void;
  refresh: () => void;
  prefetch: (href: string) => void;
};

export function useLocalizedRouter(): LocalizedRouter {
  const router = useRouter();
  const params = useParams();
  const locale = (params?.locale as Locales) || defaultLocale;

  function localizeHref(href: string): string {
    // Handle absolute URLs and external links - pass through unchanged
    if (href.startsWith("http") || href.startsWith("//")) {
      return href;
    }

    const normalizedHref = href.startsWith("/") ? href : `/${href}`;

    const segments = normalizedHref.split("/").filter(Boolean);
    const firstSegment = segments[0];

    // If URL already starts with a valid locale, don't add another one
    if (firstSegment && locales.includes(firstSegment as Locales)) {
      return normalizedHref;
    }

    return `/${locale}${normalizedHref}`;
  }

  return {
    push: (
      href: string,
      options?: Parameters<ReturnType<typeof useRouter>["push"]>[1]
    ) => {
      router.push(localizeHref(href), options);
    },
    replace: (
      href: string,
      options?: Parameters<ReturnType<typeof useRouter>["replace"]>[1]
    ) => {
      router.replace(localizeHref(href), options);
    },
    back: () => router.back(),
    forward: () => router.forward(),
    refresh: () => router.refresh(),
    prefetch: (href: string) => router.prefetch(localizeHref(href)),
  };
}
